import { createFileRoute } from "@tanstack/react-router";
import { EsRevisionSpokePage } from "@/components/site/EsRevisionSpokePage";
import { ES_REVISION_HOW_PERFORMED, ES_REVISION_SPOKE_BY_PAGE } from "@/lib/es-revision-spokes";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { imageRights, PHYSICIAN_REF } from "@/lib/medical-schema";
import { procedureImageObjects } from "@/lib/procedure-media";
import galleryDilated from "@/assets/bypass-revision-oversized-pouch-es.webp.asset.json";
import galleryDownsizing from "@/assets/bypass-revision-downsizing-es.webp.asset.json";
import galleryCompleted from "@/assets/bypass-revision-completed-pouch-es.webp.asset.json";

// The page key and path are inlined so the full Spanish revision content
// model is referenced only from the component and the server-only schema
// block below, keeping it out of the shared client bundle.
const PAGE = "revisionsBypass" as const;
const url = `${SITE}/es/cirugia-de-revision/bypass-gastrico`;
const galleryImage = (src: string, caption: string, description: string, width: number, height: number) => {
  const absolute = `${SITE}${src}`;
  return {
    "@type": "ImageObject" as const,
    url: absolute,
    contentUrl: absolute,
    caption,
    name: description,
    description,
    width,
    height,
    inLanguage: "es-US",
    ...imageRights(url),
  };
};
const PAGE_IMAGES = [
  ...procedureImageObjects(["revisionPouchContrastEs", "revisionPouchAnnotatedEs", "revisionPouchNormalEs", "revisionPouchEs", "revisionWeightChart"], "es", url),
  galleryImage(galleryDilated.url, "1. El reservorio dilatado, con la punta de la sonda calibradora visible y el exceso de estómago marcado para retirar.", "Vista laparoscópica de un reservorio de bypass gástrico sobredimensionado antes de la revisión.", 1920, 1163),
  galleryImage(galleryDownsizing.url, "2. Engrapado junto a una sonda calibradora para reducir el reservorio y retirar el exceso de estómago.", "Vista laparoscópica de la reducción del reservorio durante una revisión de bypass gástrico.", 1920, 1182),
  galleryImage(galleryCompleted.url, "3. La revisión terminada: un reservorio de tamaño adecuado, reconectado.", "Vista laparoscópica de un reservorio de bypass gástrico de tamaño adecuado después de reconectarlo.", 1920, 1075),
];

export const Route = createFileRoute("/es/cirugia-de-revision_/bypass-gastrico")({
  head: () => ({
    ...esHead(PAGE),
    scripts: import.meta.env.SSR
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  ...esWebPageSchema(PAGE),
                  "@type": "MedicalWebPage",
                  reviewedBy: PHYSICIAN_REF,
                  mainEntity: { "@id": `${url}#procedure` },
                  primaryImageOfPage: PAGE_IMAGES[1],
                  image: PAGE_IMAGES,
                },
                {
                  "@type": "MedicalProcedure",
                  "@id": `${url}#procedure`,
                  mainEntityOfPage: { "@id": `${url}#webpage` },
                  procedureType: "https://schema.org/SurgicalProcedure",
                  name: ES_REVISION_SPOKE_BY_PAGE.revisionsBypass.procedureName,
                  alternateName: ES_REVISION_SPOKE_BY_PAGE.revisionsBypass.procedureAlternateNames,
                  description: ES_REVISION_SPOKE_BY_PAGE.revisionsBypass.procedureDescription,
                  url,
                  howPerformed: ES_REVISION_HOW_PERFORMED[PAGE],
                  image: PAGE_IMAGES,
                },
                {
                  "@type": "FAQPage",
                  "@id": `${url}#faq`,
                  inLanguage: "es-US",
                  mainEntity: ES_REVISION_SPOKE_BY_PAGE.revisionsBypass.faqs.map((faq, i) => ({
                    "@type": "Question",
                    "@id": `${url}#faq-${i + 1}`,
                    name: faq.q,
                    acceptedAnswer: { "@type": "Answer", text: faq.a },
                  })),
                },
              ],
            }),
          },
        ]
      : [],
  }),
  component: EsBypassRevision,
});

function EsBypassRevision() {
  return <EsRevisionSpokePage spoke={ES_REVISION_SPOKE_BY_PAGE.revisionsBypass} />;
}
